import {action, computed, makeObservable, observable, runInAction} from 'mobx';
import {showSnackbar, SnackbarType} from 'webapp-lib/Functions';

import notificationsBackend, {
  Notification,
  NotificationCenterPushNotification,
  NotificationSettings,
  NotificationsResponse,
  NotificationSummary,
} from '@lexian/api/notifications-api';
import asyncBackend from '@lexian/api/notifications-async-backend';
import i18n from '_/i18n';
import {LoggedInUserInformation} from '_/utilities';
import {notifyDesktop} from '_/utilities/desktop-notifications';
import {Loadable, State} from '_/utilities/types';

export type NewObligationState = Parameters<
  typeof notificationsBackend.resolveObligationVersionDeadlineReachedNotification
>[0]['parameters']['body']['obligationState'];

export default class Store {
  public settings: NotificationSettings;

  public notifications: Loadable<Notification[]>;

  public summary: Loadable<NotificationSummary>;

  private refreshOnUpdate = false;

  public activeNotification?: Notification;

  public loggedInUser: LoggedInUserInformation;

  private unsubscribeNotificationSocket?: () => void;

  public readonly eventTarget: EventTarget = new EventTarget();

  public get shownNotifications(): (Notification & {section?: string})[] {
    if (this.notifications.data == null) {
      return [];
    }

    const notificationContextsLabels: Record<Notification['context'], string> =
      {
        CASUS_MANAGEMENT: i18n.t('common:modules.casus'),
        DOCUMENT_MANAGEMENT: i18n.t('common:modules.documents'),
        INVOICE_MANAGEMENT: i18n.t('common:modules.invoicing'),
        OBLIGATION_MANAGEMENT: i18n.t('common:modules.obligations'),
        TASK_MANAGEMENT: i18n.t('common:modules.task'),
        TIMETRACKING: i18n.t('common:modules.timetracking'),
      };

    return (
      this.settings.grouped
        ? [...this.notifications.data].sort((item1, item2) =>
            item1.context < item2.context ? -1 : 0
          )
        : this.notifications.data
    ).map((notification) => ({
      ...notification,
      section: this.settings.grouped
        ? `${notificationContextsLabels[notification.context]} (${
            this.notifications.data!.filter(
              (item) => item.context === notification.context
            ).length
          })`
        : undefined,
    }));
  }

  public constructor(loggedInUser: LoggedInUserInformation) {
    makeObservable<
      Store,
      'refreshOnUpdate' | 'initializeSocket' | 'updateWithNotifications'
    >(this, {
      settings: observable,
      notifications: observable,
      summary: observable,
      refreshOnUpdate: observable,
      activeNotification: observable,
      loggedInUser: observable,
      shownNotifications: computed,
      initializeSocket: action.bound,
      setModuleState: action.bound,
      setActiveNotification: action.bound,
      updateWithNotifications: action.bound,
      fetchNotifications: action.bound,
      fetchSummary: action.bound,
      updateSettings: action.bound,
      setReadFlag: action.bound,
      setImportantFlag: action.bound,
      deleteNotification: action.bound,
      clearNotifications: action.bound,
      setDelay: action.bound,
      resolveTask: action.bound,
      resolveObligation: action.bound,
    });

    this.loggedInUser = loggedInUser;
    this.settings = {
      grouped: false,
      showOnlyImportant: false,
      visibleNotifications: 'ALL',
    };
    this.notifications = {state: State.Loading};
    this.summary = {
      data: {
        hasImportantNotification: false,
        unreadInformativeNotifications: 0,
        unreadResolvableNotifications: 0,
      },
      state: State.Loading,
    };
    asyncBackend.initializeOnConnect(this.initializeSocket);

    this.fetchNotifications();
    this.fetchSummary();
  }

  public unsubscribe(): void {
    this.unsubscribeNotificationSocket?.();
  }

  private initializeSocket(): void {
    this.unsubscribeNotificationSocket =
      asyncBackend.topicNotificationUserSpecific(
        {userId: this.loggedInUser.userId},
        ({body}) => {
          if (
            body.pushNotificationType !== 'NotificationCenterPushNotification'
          ) {
            return;
          }
          const {notificationSummary, addedNotifications} =
            body as NotificationCenterPushNotification;
          if (this.refreshOnUpdate) {
            this.fetchNotifications();
          }
          if (addedNotifications) {
            notifyDesktop(
              i18n.t('notifications:desktopNotifications.newNotification'),
              {
                body: i18n
                  .t('notifications:desktopNotifications.newNotificationBody')
                  .toString(),
                icon: '/apple-touch-icon.png',
              }
            );
          }
          runInAction(() => {
            this.summary = {
              ...this.summary,
              data: notificationSummary,
            };
          });
        }
      ).unsubscribe;
  }

  public setModuleState(isOpen: boolean): void {
    this.refreshOnUpdate = isOpen;
  }

  public setActiveNotification(
    activeNotification: Notification | undefined
  ): void {
    this.activeNotification = activeNotification;
  }

  private updateWithNotifications(
    response: NotificationsResponse,
    state = State.Success
  ): void {
    runInAction(() => {
      this.summary = {
        ...this.summary.abort,
        data: response.summary,
        state: state || this.notifications.state,
      };
      this.notifications = {
        ...this.notifications,
        data: response.notifications,
        state: state || this.notifications.state,
      };
      this.settings = response.settings;
      this.setActiveNotification(
        response.notifications.find(
          (item) => item.id === this.activeNotification?.id
        )
      );
    });
  }

  public async fetchNotifications(): Promise<void> {
    this.notifications.abort?.();
    this.notifications = {
      ...this.notifications,
      state: State.Loading,
    };

    await notificationsBackend.getNotifications({
      parameters: {},
      abortCallbackSetter: (abortCallback) => {
        this.notifications.abort = abortCallback;
      },
      onSuccess: ({body}) => {
        this.updateWithNotifications(body, State.Success);
      },
      afterErrorHandler: () => {
        runInAction(() => {
          this.notifications = {
            ...this.notifications,
            state: State.Error,
          };
        });
      },
    });
  }

  public async fetchSummary(): Promise<void> {
    this.summary.abort?.();
    this.summary = {
      ...this.summary,
      state: State.Loading,
    };

    await notificationsBackend.getNotificationSummary({
      parameters: {},
      abortCallbackSetter: (abortCallback) => {
        this.summary.abort = abortCallback;
      },
      onSuccess: ({body}) => {
        runInAction(() => {
          this.summary = {
            ...this.summary,
            data: body.notificationSummary,
            state: State.Success,
          };
        });
      },
      afterErrorHandler: () => {
        runInAction(() => {
          this.summary = {
            ...this.summary,
            state: State.Error,
          };
        });
      },
    });
  }

  public async updateSettings(settings: NotificationSettings): Promise<void> {
    this.notifications.state = State.Loading;
    await notificationsBackend.updateSettings({
      parameters: {body: settings},
      onSuccess: ({body}) => {
        this.updateWithNotifications(body, State.Success);
      },
    });
  }

  public async setReadFlag({
    notificationId,
    newFlag,
  }: {
    notificationId: string;
    newFlag: boolean;
  }): Promise<void> {
    if (newFlag) {
      await notificationsBackend.markRead({
        parameters: {notificationId},
        onSuccess: ({body}) => {
          this.updateWithNotifications(body);
        },
      });
    } else {
      await notificationsBackend.markUnread({
        parameters: {notificationId},
        onSuccess: ({body}) => {
          this.updateWithNotifications(body);
        },
      });
    }
  }

  public async setImportantFlag({
    notificationId,
    newFlag,
  }: {
    notificationId: string;
    newFlag: boolean;
  }): Promise<void> {
    if (newFlag) {
      await notificationsBackend.markImportant({
        parameters: {notificationId},
        onSuccess: ({body}) => {
          this.updateWithNotifications(body);
        },
      });
    } else {
      await notificationsBackend.markUnimportant({
        parameters: {notificationId},
        onSuccess: ({body}) => {
          this.updateWithNotifications(body);
        },
      });
    }
  }

  public async deleteNotification({
    notificationId,
  }: {
    notificationId: string;
  }): Promise<void> {
    await notificationsBackend.deleteNotification({
      parameters: {notificationId},
      onSuccess: ({body}) => {
        this.updateWithNotifications(body);
        showSnackbar({
          text: i18n.t('notifications:snackbar.success.deleteNotification'),
          type: SnackbarType.DELETE,
        });
      },
    });
  }

  public async clearNotifications(): Promise<void> {
    await notificationsBackend.deleteAllInformativeNotifications({
      parameters: {},
      onSuccess: ({body}) => {
        this.updateWithNotifications(body);
        showSnackbar({
          text: i18n.t(
            'notifications:snackbar.success.clearInformativeNotifications'
          ),
        });
      },
    });
  }

  public async setDelay({
    notificationId,
    newDate,
  }: {
    notificationId: string;
    newDate: string;
  }): Promise<void> {
    await notificationsBackend.delay({
      parameters: {
        body: {notifyAt: newDate},
        notificationId,
      },
      onSuccess: ({body}) => {
        this.updateWithNotifications(body);
        showSnackbar({
          text: i18n.t('notifications:snackbar.success.setDelay'),
        });
        this.setActiveNotification(undefined);
      },
    });
  }

  public async resolveTask({
    notificationId,
    rescheduleDate,
  }: {
    notificationId: string;
    rescheduleDate?: string;
  }): Promise<void> {
    await notificationsBackend.resolveTaskDueDateReachedNotification({
      parameters: {
        body: {
          rescheduleDate,
          resolution: rescheduleDate ? 'RESCHEDULE' : 'COMPLETE',
          type: 'TaskDueDateReachedNotification',
        },
        notificationId,
      },
      onSuccess: () => {
        showSnackbar({
          text: rescheduleDate
            ? i18n.t('notifications:snackbar.success.rescheduleTask')
            : i18n.t('notifications:snackbar.success.completeTask'),
        });
        this.setActiveNotification(undefined);
        this.fetchNotifications();
      },
    });
  }

  public async resolveObligation({
    notificationId,
    newState,
  }: {
    notificationId: string;
    newState: NewObligationState;
  }): Promise<void> {
    await notificationsBackend.resolveObligationVersionDeadlineReachedNotification(
      {
        parameters: {
          body: {
            obligationState: newState,
            resolution: 'CHANGE_STATE',
            type: 'ResolvableObligationVersionDeadlineReachedNotification',
          },
          notificationId,
        },
        onSuccess: () => {
          showSnackbar({
            text: i18n.t('notifications:snackbar.success.resolveObligation'),
          });
          this.setActiveNotification(undefined);
          this.fetchNotifications();
        },
      }
    );
  }
}

export const downloadZipFile = async ({
  zipFileId,
}: {
  zipFileId: string;
}): Promise<void> => {
  await notificationsBackend.downloadZipFile({
    parameters: {zipFileId},
    onSuccess: () => {
      window.location.assign(
        notificationsBackend.downloadZipFileURL({zipFileId})
      );
    },
    localHandlers: {
      404: () => {
        showSnackbar({
          text: i18n.t('notifications:snackbar.error.downloadLinkExpired'),
          type: SnackbarType.WARNING,
        });
      },
    },
  });
};
